import { useEffect, useState } from "react";
import {
  Plus,
  Edit,
  Trash2,
  LogIn,
  Upload,
  CheckCircle2,
  Settings as SettingsIcon,
  Activity,
} from "lucide-react";
import api from "@/lib/api";

const ACTION_META = {
  create: { label: "Oluşturuldu", color: "bg-green-50 text-green-700 border-green-200", icon: Plus },
  update: { label: "Güncellendi", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Edit },
  delete: { label: "Silindi", color: "bg-red-50 text-red-700 border-red-200", icon: Trash2 },
  login: { label: "Giriş Yapıldı", color: "bg-slate-100 text-slate-700 border-slate-200", icon: LogIn },
  upload: { label: "Yüklendi", color: "bg-purple-50 text-purple-700 border-purple-200", icon: Upload },
  mark_read: { label: "Okundu Olarak İşaretlendi", color: "bg-amber-50 text-amber-700 border-amber-200", icon: CheckCircle2 },
};

const ENTITY_LABEL = {
  product: "Ürün",
  category: "Kategori",
  message: "Mesaj",
  settings: "Ayarlar",
  upload: "Görsel",
  auth: "Yönetici",
};

export default function AdminAuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState("");
  const [filterEntity, setFilterEntity] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filterAction) params.action = filterAction;
    if (filterEntity) params.entity_type = filterEntity;
    try {
      const { data } = await api.get("/admin/audit-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterEntity]);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-audit-logs-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="heading-font text-3xl text-[#0A2540] flex items-center gap-3">
            <Activity size={28} className="text-[#0066FF]" />
            İşlem Kayıtları
          </h1>
          <p className="text-sm text-slate-500 mt-1">Yönetici panelinde gerçekleştirilen tüm işlemlerin kaydı.</p>
        </div>
        <div className="text-xs text-slate-500 bg-white border border-slate-200 px-3 py-2">
          Toplam: <span className="font-semibold text-[#0A2540]">{total}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <span className="text-xs uppercase tracking-wider text-slate-500">Filtreler:</span>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="px-3 py-2 border border-slate-200 focus:border-[#0066FF] outline-none text-sm bg-white"
          data-testid="audit-filter-action"
        >
          <option value="">Tüm İşlemler</option>
          {Object.entries(ACTION_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={filterEntity}
          onChange={(e) => setFilterEntity(e.target.value)}
          className="px-3 py-2 border border-slate-200 focus:border-[#0066FF] outline-none text-sm bg-white"
          data-testid="audit-filter-entity"
        >
          <option value="">Tüm Türler</option>
          {Object.entries(ENTITY_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {(filterAction || filterEntity) && (
          <button
            onClick={() => {
              setFilterAction("");
              setFilterEntity("");
            }}
            className="text-xs text-[#0066FF] hover:underline"
            data-testid="audit-clear-filters"
          >
            Filtreleri temizle
          </button>
        )}
      </div>

      {/* Logs */}
      <div className="bg-white border border-slate-200">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Yükleniyor...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="audit-empty">
            Henüz kayıt yok.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((log) => {
              const meta = ACTION_META[log.action] || { label: log.action, color: "bg-slate-100 text-slate-700 border-slate-200", icon: Activity };
              const Icon = meta.icon;
              return (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-5 hover:bg-slate-50 transition-colors"
                  data-testid={`audit-row-${log.id}`}
                >
                  <div className={`w-10 h-10 border ${meta.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${meta.color}`}>
                        {meta.label}
                      </span>
                      <span className="text-xs uppercase tracking-wider text-slate-400">
                        {ENTITY_LABEL[log.entity_type] || log.entity_type}
                      </span>
                    </div>
                    <div className="mt-2 text-[#0A2540] font-medium truncate">
                      {log.entity_name || log.entity_id || "—"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                      <span>{log.user_email}</span>
                      <span>·</span>
                      <span>{fmtDate(log.created_at)}</span>
                      {log.details?.fields && (
                        <>
                          <span>·</span>
                          <span className="text-slate-400">
                            Alanlar: {log.details.fields.join(", ")}
                          </span>
                        </>
                      )}
                      {log.details?.size && (
                        <>
                          <span>·</span>
                          <span className="text-slate-400">
                            {(log.details.size / 1024).toFixed(0)} KB
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
